import React, { useCallback, useMemo, useState } from 'react'
import Router from 'next/router'
import styled from 'styled-components'
import * as R from 'ramda'
import PointBalance from './PointBalance'
import RedeemSKUButton from './RedeemSKUButton'
import Tooltip from 'components/UI/TooltipV2'
import { bindPhoneThenReload } from 'utils/sdkUtil'
import { formatNumerics } from 'utils/formatUtil'
import { loginOrGetToken } from 'utils/loginUtil'
import { mobileSize } from 'theme'
import { openLinkBlank } from 'utils/appSchemeUtil'
import { useTrackingStore } from 'store/trackingStore'
import { COUPON_REDEEMABLE_STATUS } from '/constants/coupon'
import { COUPON_SCREEN_NAME, EVENT_ACTION, BUTTON_NAME } from 'tracking'
import { useUserAgentStore } from 'store/userAgentStore'
import RedeemBubble from './RedeemBubble'
import Drawer from '@material-ui/core/Drawer'

const LINK_TO_HOW_TO_GET_POINTS = encodeURI(
  'https://www.hk01.com/獎賞專區/371302/香港01會員獎賞計劃'
)

const SkuButton = ({
  isLoggedIn,
  hasBindPhone,
  isFetchingProfile,
  amount,
  pointsValue,
  redeemQuota,
  redeemProfile,
  redeemCoupon,
  hasRedeemed,
  skuData,
  fastShare,
  isMobileBreakpoint,
  paymentIntegration,
}) => {
  const [confirmTooltip, setConfirmTooltip] = useState(false)
  const [openTooltip, setOpenTooltip] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const { trackEvent } = useTrackingStore()
  const { userRemainQuota, redeemableStatus, userPoints } = redeemProfile
  const isFree = pointsValue <= 0 && !amount

  const userAgentStore = useUserAgentStore()
  const { isPWA } = userAgentStore

  const onLoginToRedeemCoupon = useCallback(() => {
    trackEvent(EVENT_ACTION.CLICK, {
      screen: COUPON_SCREEN_NAME.SKU,
      button: BUTTON_NAME.LOGIN,
    })
    const loginParams = { pathname: window.location.pathname }
    // TODO: refactor to util and make it testable
    switch (true) {
      case fastShare && isPWA():
        loginParams.loginSource = 'fastShare-PWA-sku'
        break
      case !!fastShare:
        loginParams.loginSource = 'fastShare-sku'
        break
      case isPWA():
        loginParams.loginSource = 'PWA-sku'
        break
    }

    return loginOrGetToken(loginParams)
  }, [fastShare, isPWA, trackEvent])
  const closeTooltip = useCallback(() => setOpenTooltip(false), [])
  const onBindPhoneToRedeemCoupon = useCallback(async () => {
    trackEvent(EVENT_ACTION.CLICK, {
      screen: COUPON_SCREEN_NAME.SKU,
      button: BUTTON_NAME.BIND_PHONE,
    })
    // TODO: refactor to util and make it testable
    const trackSource = () => {
      let trackSourceValue
      switch (true) {
        case fastShare && isPWA():
          trackSourceValue = 'fastShare-PWA-sku'
          break
        case !!fastShare:
          trackSourceValue = 'fastShare-sku'
          break
        case isPWA():
          trackSourceValue = 'PWA-sku'
          break
      }
      return trackSourceValue
    }
    await bindPhoneThenReload(trackSource())
  }, [fastShare, isPWA, trackEvent])
  const { stat: stockInfo } = skuData
  const { availableNum } = stockInfo

  const canRedeem = redeemableStatus === COUPON_REDEEMABLE_STATUS.REDEEM_OK

  const notEnoughPoints =
    redeemableStatus === COUPON_REDEEMABLE_STATUS.INSUFFICIENT_POINTS
  const showRemainingQuota = !R.isNil(userRemainQuota) && canRedeem
  const {
    tooltipText,
    onClickTooltip,
    customColor,
    showShadow,
    enableBackdropClick,
  } = useMemo(() => {
    const showRemainingQuota = canRedeem && hasRedeemed
    if (showRemainingQuota) {
      return {
        customColor: '#252525',
        tooltipText: `已兌換${redeemQuota - userRemainQuota}次`,
      }
    }

    if (!showRemainingQuota && redeemProfile.userRemainQuota === 0) {
      return {
        tooltipText: '查看我的優惠券',
        onClickTooltip: () => {
          Router.push('/mycoupon')
        },
      }
    }

    if (notEnoughPoints) {
      if (!isFetchingProfile) {
        setOpenTooltip(true)
      }
      return {
        tooltipText: (
          <InsufficientPointsLabel>
            積分不足? 查看如何賺取積分﹥
          </InsufficientPointsLabel>
        ),
        onClickTooltip: () => {
          openLinkBlank(LINK_TO_HOW_TO_GET_POINTS)
        },
      }
    }

    if (confirmTooltip) {
      return {
        tooltipText: (
          <RedeemBubble
            redeemCoupon={redeemCoupon}
            amount={amount}
            pointsValue={pointsValue}
            userPoints={userPoints}
            setConfirmTooltip={setConfirmTooltip}
            setOpenTooltip={setOpenTooltip}
            userRemainQuota={userRemainQuota}
            skuData={skuData}
            trackEvent={trackEvent}
          />
        ),
        customColor: '#ffffff',
        showShadow: true,
        enableBackdropClick: true,
      }
    }

    return {}
  }, [
    canRedeem,
    hasRedeemed,
    redeemProfile.userRemainQuota,
    notEnoughPoints,
    confirmTooltip,
    redeemQuota,
    userRemainQuota,
    isFetchingProfile,
    redeemCoupon,
    amount,
    pointsValue,
    userPoints,
    skuData,
    trackEvent,
  ])
  const disableRedeemButton =
    availableNum === 0 ||
    (!canRedeem && isLoggedIn && hasBindPhone) ||
    isFetchingProfile

  const handleClickRedeem = useCallback(() => {
    if (isMobileBreakpoint) {
      setModalVisible(true)
    } else {
      setConfirmTooltip(true)
      setOpenTooltip(true)
    }
  }, [isMobileBreakpoint])

  const closeConfirmModal = useCallback(() => setModalVisible(false), [])

  const onClickButton = !isLoggedIn
    ? onLoginToRedeemCoupon
    : !hasBindPhone
    ? onBindPhoneToRedeemCoupon
    : handleClickRedeem

  return (
    <>
      <RedeemButtonWapper>
        <Tooltip
          title={
            <TooltipTitleWrapper
              {...(onClickTooltip && {
                onClick: onClickTooltip,
              })}
            >
              {tooltipText}
            </TooltipTitleWrapper>
          }
          placement="bottom"
          arrow
          customColor={customColor}
          showShadow={showShadow}
          open={openTooltip}
          onClose={closeTooltip}
          enableBackdropClick={enableBackdropClick}
        >
          <RedeemSKUButton
            disabled={disableRedeemButton || openTooltip}
            {...(!disableRedeemButton && {
              onClick: onClickButton,
            })}
            isFetchingProfile={isFetchingProfile}
          >
            <RedeemButtonText
              redeemQuota={redeemQuota}
              status={redeemableStatus}
              isLoggedIn={isLoggedIn}
              hasBindPhone={hasBindPhone}
              isFree={isFree}
              pointsValue={pointsValue}
              showRemainingQuota={showRemainingQuota}
              userRemainQuota={userRemainQuota}
              availableNum={availableNum}
            />
          </RedeemSKUButton>
        </Tooltip>
        <PointBalance
          points={formatNumerics(userPoints || 0)}
          isFetchingProfile={isFetchingProfile}
          isLoggedIn={isLoggedIn}
          hasBindPhone={hasBindPhone}
          paymentIntegration={paymentIntegration}
        />
      </RedeemButtonWapper>
      <Drawer anchor="bottom" open={modalVisible} onClose={closeConfirmModal}>
        <RedeemBubble
          redeemCoupon={redeemCoupon}
          amount={amount}
          pointsValue={pointsValue}
          userPoints={userPoints}
          setConfirmTooltip={setConfirmTooltip}
          setOpenTooltip={setOpenTooltip}
          redeemQuota={redeemQuota}
          userRemainQuota={userRemainQuota}
          skuData={skuData}
          trackEvent={trackEvent}
          inDraw
        />
      </Drawer>
    </>
  )
}

const RedeemButtonText = ({
  redeemQuota,
  status,
  isLoggedIn,
  hasBindPhone,
  isFree,
  pointsValue,
  userRemainQuota,
  availableNum,
}) => {
  if (availableNum === 0) {
    return `兌換完畢`
  }
  if (!isLoggedIn) {
    return `手機號碼登入以兌換`
  }
  if (!hasBindPhone) {
    return `綁定手機以兌換`
  }
  return (
    <>
      <RedeemStatusText
        redeemQuota={redeemQuota}
        status={status}
        isFree={isFree}
        pointsValue={pointsValue}
      />
      {redeemQuota && (
        <LimitText>
          限兌換
          {redeemQuota}次
          {!R.isNil(userRemainQuota) &&
            userRemainQuota !== redeemQuota &&
            ` | 已兌換${redeemQuota - userRemainQuota}次`}
        </LimitText>
      )}
    </>
  )
}

const RedeemStatusText = ({ status, isFree }) =>
  ({
    OUT_OF_STOCK: '兌換完畢',
    INSUFFICIENT_POINTS: '積分不足',
    NO_QUOTA: '已兌換',
    REDEEM_OK: isFree ? '免費兌換' : '立即兌換',
    DEFAULT: '兌換處理中',
  }[status])

const TooltipTitleWrapper = styled.div`
  user-select: none;

  /* revert disabled pointer-events of mui tooltip */
  pointer-events: auto;
`

const RedeemButtonWapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 24px;
  @media ${mobileSize} {
    justify-content: space-between;
    margin-top: 0;
    box-sizing: border-box;
    padding: 8px 12px;
    border-radius: 2px;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fff;
    flex-direction: row-reverse;
    box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.05);
  }
`
const LimitText = styled.p`
  font-size: 12px;
  font-weight: 300;
  padding: 0;
  margin: 0;
`

const InsufficientPointsLabel = styled.div`
  cursor: pointer;
  font-size: 12px;
  font-weight: normal;
`
export default SkuButton
